import { forwardRef, useImperativeHandle } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { z } from 'zod';

import { FormImage } from '@/components/common/form-inputs/FormImage';
import { FormInput } from '@/components/common/form-inputs/FormInput';
import { FormMultiSelect } from '@/components/common/form-inputs/FormMultiSelect';
import { MyProfileFormSchema } from '@/components/forms/schemas/MyProfileFormSchema';
import { Form } from '@/components/ui/form';
import { US_STATES } from '@/constants/territories/states';

interface MyProfileFormProps {
  form: UseFormReturn<z.infer<typeof MyProfileFormSchema>>;
}

export const MyProfileForm = forwardRef<unknown, MyProfileFormProps>(
  ({ form }, ref) => {
    useImperativeHandle(ref, () => ({
      submitForm: () => {
        form.handleSubmit(async () => {})();
      },
    }));

    return (
      <Form {...form}>
        <form className="w-full space-y-1.5">
          <div className="flex items-center gap-6 rounded-xl border border-border bg-white p-6 max-md:flex-col">
            <div className="as-title-03 as-strong text-zinc-700 max-md:order-2 max-md:text-center">
              Profile photo
              <p className="as-title-03 font-normal">
                This will be displayed in the application
              </p>
            </div>
            <div className="max-md:order-1 lg:min-w-[280px]">
              <FormImage
                name="photo_url"
                isEdit={true}
                control={form.control}
                setValue={form.setValue}
              />
            </div>
          </div>
          <div className="flex flex-col gap-6 rounded-xl border border-border bg-white p-6">
            <p className="as-title-02 as-strong w-[280px] font-medium text-zinc-700">
              Contact Details
            </p>
            <div className="grid flex-1 grid-cols-2 gap-6 max-sm:grid-cols-1">
              <FormInput
                type="text"
                name="first_name"
                label="First Name"
                control={form.control}
              />
              <FormInput
                type="text"
                name="last_name"
                label="Last Name"
                control={form.control}
              />
              <FormInput
                type="text"
                name="phone"
                label="Phone"
                control={form.control}
              />
              <FormInput
                type="email"
                name="email"
                label="Email"
                control={form.control}
              />
              <FormInput
                type="text"
                name="address"
                label="Address"
                control={form.control}
              />
              <FormInput
                type="text"
                name="city"
                label="City"
                control={form.control}
              />
              <FormInput
                type="text"
                name="state"
                label="State"
                placeholder="e.g. NY"
                maxLength={2}
                control={form.control}
                onFormat={(e) => e.target.value.toUpperCase()}
              />
              <FormInput
                type="text"
                name="zip_code"
                label="Zip Code"
                control={form.control}
              />
              <FormInput
                type="date"
                name="birth_date"
                label="Birth Date"
                control={form.control}
              />
              <FormInput
                name="linkedin"
                label="LinkedIn"
                control={form.control}
              />
            </div>
          </div>
          <div className="flex items-center gap-6 rounded-xl border border-border bg-white p-6 max-md:flex-col max-md:items-start">
            <div className="as-title-03 as-strong min-w-[280px] text-zinc-700">
              Preferred Market
              <p className="as-title-03 font-normal">You can select 5 max.</p>
            </div>
            <FormMultiSelect
              name="preferred_markets"
              control={form.control}
              options={Object.entries(US_STATES).map(([key, value]) => ({
                value: key,
                label: value,
              }))}
              className="w-full"
              maxSelections={5}
              placeholder="Select up to 5 markets"
            />
          </div>
        </form>
      </Form>
    );
  }
);
